import { create } from "zustand";
import { api } from "../api";
import { getAssetCategory, getAssetManagementType } from "../utils/assetTaxonomy";

export function isNetworkAsset(item) {
  if (!item) return false;
  const cat = getAssetCategory(item);
  const mgmt = getAssetManagementType(item);
  const openPorts = item?.open_ports || item?.openPorts || item?.specs?.open_ports || [];
  return (
    ["router", "switch", "firewall", "load_balancer", "network", "network_asset"].includes(cat) ||
    mgmt === "agentless_snmp" ||
    openPorts.includes(161)
  );
}

export function mergeRouterAssets(cmdbNetworkAssets, routersApiAssets) {
  const mergedMap = new Map();

  (cmdbNetworkAssets || []).forEach((asset) => {
    if (!asset) return;
    const key = (asset.id || asset.hostname || asset.name || "").toLowerCase();
    if (key) mergedMap.set(key, asset);
  });

  (routersApiAssets || []).forEach((router) => {
    if (!router) return;
    const key = (router.id || router.hostname || router.name || "").toLowerCase();
    if (mergedMap.has(key)) {
      const existing = mergedMap.get(key);
      mergedMap.set(key, {
        ...router,
        ...existing,
        // Preserve CMDB metadata, but use the routers API as the source of
        // truth for router status and health score.
        status: router.status ?? existing.status,
        health_status: router.health_status ?? existing.health_status,
        health_score: router.health_score ?? existing.health_score,
        health_percent: router.health_score ?? existing.health_percent,
        specs: {
          ...(router.specs || {}),
          ...(existing.specs || {}),
          health_score:
            router.health_score ??
            router.specs?.health_score ??
            existing.specs?.health_score,
        },
      });
    } else {
      mergedMap.set(key, router);
    }
  });

  return Array.from(mergedMap.values());
}

export const useAssetStore = create((set, get) => ({
  // State
  servers: [],
  routers: [],
  discoveredAssets: [],
  summaryStats: null,
  loading: false,
  error: null,
  lastUpdated: null,

  // Fetch Network Data (/network/routers & /cmdb/servers & /network/summary)
  fetchNetworkData: async (isSilent = false) => {
    if (!isSilent) set({ loading: true, error: null });
    try {
      const [serversData, routersData, summaryData] = await Promise.all([
        api("/cmdb/servers?limit=100"),
        api("/network/routers"),
        api("/network/summary"),
      ]);
      const serversList = Array.isArray(serversData?.items)
        ? serversData.items
        : Array.isArray(serversData)
        ? serversData
        : [];
      const networkFromCmdb = serversList.filter(isNetworkAsset);
      const combinedRouters = mergeRouterAssets(networkFromCmdb, routersData);

      set({
        servers: serversList,
        routers: combinedRouters,
        summaryStats: summaryData || null,
        lastUpdated: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      if (!isSilent) {
        set({
          error: "Failed to load network infrastructure data.",
          loading: false,
        });
      }
    }
  },

  // Fetch CMDB Servers Data (/cmdb/servers)
  fetchCmdbData: async (isSilent = false) => {
    if (!isSilent) set({ loading: true, error: null });
    try {
      const serversData = await api("/cmdb/servers?limit=100");
      const serversList = Array.isArray(serversData?.items)
        ? serversData.items
        : Array.isArray(serversData)
        ? serversData
        : [];
      const networkFromCmdb = serversList.filter(isNetworkAsset);
      set({
        servers: serversList,
        routers: mergeRouterAssets(networkFromCmdb, get().routers),
        lastUpdated: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      if (!isSilent) {
        set({ error: "Failed to load CMDB servers.", loading: false });
      }
    }
  },

  // Fetch Staging Discovered Assets Data (/discovery/assets)
  fetchDiscoveryData: async (isSilent = false) => {
    if (!isSilent) set({ loading: true, error: null });
    try {
      const assetsData = await api("/discovery/assets");
      set({
        discoveredAssets: assetsData || [],
        lastUpdated: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      if (!isSilent) {
        set({ error: "Failed to load discovered assets.", loading: false });
      }
    }
  },

  // Master Parallel Fetch (All Views Synced Simultaneously)
  fetchAllData: async (isSilent = false) => {
    if (!isSilent) set({ loading: true, error: null });
    try {
      const [routersData, summaryData, serversData, assetsData] =
        await Promise.allSettled([
          api("/network/routers"),
          api("/network/summary"),
          api("/cmdb/servers?limit=100"),
          api("/discovery/assets"),
        ]);

      const routersRequestFailed =
        routersData.status !== "fulfilled" ||
        !Array.isArray(routersData.value);

      const rawServers =
        serversData.status === "fulfilled"
          ? serversData.value?.items ||
            (Array.isArray(serversData.value) ? serversData.value : [])
          : get().servers;

      const rawRouters = routersRequestFailed
        ? get().routers
        : routersData.value;

      const networkFromCmdb = rawServers.filter(isNetworkAsset);
      const combinedRouters = mergeRouterAssets(networkFromCmdb, rawRouters);

      set({
        servers: rawServers,
        routers: combinedRouters,
        summaryStats:
          summaryData.status === "fulfilled"
            ? summaryData.value || null
            : get().summaryStats,
        discoveredAssets:
          assetsData.status === "fulfilled"
            ? assetsData.value || []
            : get().discoveredAssets,
        error: routersRequestFailed
          ? "Could not load network appliance data."
          : null,
        lastUpdated: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      if (!isSilent) {
        set({ error: "Global infrastructure refresh error.", loading: false });
      }
    }
  },

  // Real-time Stream & WebSocket Atomic Patch Action
  updateLiveTelemetry: (hostnameOrIp, telemetryPatch) => {
    const key = (hostnameOrIp || "").toLowerCase();
    if (!key) return;

    set((state) => {
      const matchKey = (item) =>
        (item?.hostname || "").toLowerCase() === key ||
        (item?.ip_address || "").toLowerCase() === key ||
        (item?.specs?.ip_address || "").toLowerCase() === key;

      const routersArr = Array.isArray(state.routers) ? state.routers : [];
      const updatedRouters = routersArr.map((r) => {
        if (matchKey(r)) {
          return {
            ...r,
            ...telemetryPatch,
            specs: { ...(r.specs || {}), ...(telemetryPatch.specs || {}) },
          };
        }
        return r;
      });

      const serversArr = Array.isArray(state.servers) ? state.servers : [];
      const updatedServers = serversArr.map((s) => {
        if (matchKey(s)) {
          return {
            ...s,
            ...telemetryPatch,
            specs: { ...(s.specs || {}), ...(telemetryPatch.specs || {}) },
          };
        }
        return s;
      });

      const assetsArr = Array.isArray(state.discoveredAssets)
        ? state.discoveredAssets
        : [];
      const updatedAssets = assetsArr.map((a) => {
        if (matchKey(a)) {
          return {
            ...a,
            ...telemetryPatch,
            specs: { ...(a.specs || {}), ...(telemetryPatch.specs || {}) },
          };
        }
        return a;
      });

      return {
        routers: updatedRouters,
        servers: updatedServers,
        discoveredAssets: updatedAssets,
        lastUpdated: new Date().toISOString(),
      };
    });
  },
}));
